<!--
  @fileoverview KeyReply Webchat Root Component

  This is the root component that orchestrates the entire webchat widget.
  It manages the overall layout, state coordination, and global features including:

  @component KeyReply
  @displayName Webchat Root App

  Main Features:
  - Launcher button with notification badge
  - Multi-app icon selector (bot, live chat, etc.)
  - Preview message bubble (proactive engagement)
  - Chat window container
  - Lightbox for iframe content
  - JWT session expiry drawer/dialog
  - Widget dragging functionality
  - RTL layout support
  - Navigate away prompt

  Layout Structure:
  1. keyreply-container - Main wrapper (left/right position)
  2. keyreply-launcher-wrapper - Launcher button area
     - Launcher component
     - AppIcon components for each enabled channel
     - Preview bubble card
  3. ChatWindow component (conditional)
  4. Lightbox dialog for iframe content
  5. JWT expiry drawer/dialog (mobile vs desktop)

  @techdebt
  - None currently - wheel event handler moved to ChatWindow, drag functionality extracted to useWidgetDrag composable

  @vue3migration - COMPLETED
  - Migrated from Vuex to Pinia store (useMainStore)
  - Replaced slot="name" with #name syntax
  - Replaced :visible.sync with v-model:visible
  - beforeunload event listener should be cleaned up in beforeUnmount
  - Replace $createElement with h() function
  - Replace $confirm with Element Plus equivalent
-->
<template>
  <!--
    Main webchat container
    Position class determines left/right placement
    'keyreply-opened' class applied when chat window is visible
    'ui-beta' class enables alternative UI styling
  -->
  <div
    id="keyreply-container"
    ref="container"
    :class="{
      'keyreply-left': settings.position === 'left',
      'keyreply-right': settings.position !== 'left',
      'keyreply-opened': !!store.chatWindow,
      'ui-beta': isUiBetaEnabled,
    }"
  >
    <div
      class="keyreply-launcher-wrapper"
      id="keyreply-launcher"
      @mousedown="handleGrabElement({ event: $event, elementId: 'keyreply-launcher' })"
      @touchstart="handleGrabElement({ event: $event, elementId: 'keyreply-launcher' })"
    >
      <launcher
        ref="launcher"
        v-if="displayLauncher"
        @click="clickedLauncher"
        :isLauncherCanClick="!isElementReadyToDrag"
      />
      <app-icon
        v-for="(app, index) in visibleApps"
        :key="app.id"
        :show="true"
        :app="app"
        :data="app.data"
        :index="index"
      />
      <transition name="el-zoom-in-bottom">
        <el-card
          v-if="
            !chatWindow &&
            previewBubble.enabled &&
            showMessagePreview &&
            displayLauncher &&
            !store.webchatUsersIsAtMaxLimit
          "
          class="keyreply-popup-bubble"
        >
          <div @click="clickLauncher">
            <div
              id="closeButton1"
              class="relative -top-[10px] -right-[10px] float-right cursor-pointer"
              @click="closeMessagePreview($event)"
            >
              <el-icon><Close /></el-icon>
            </div>
            {{ previewBubble.message }}
          </div>
        </el-card>

        <el-card
          v-else-if="store.webchatUsersIsAtMaxLimit && store.showMessagePreview"
          class="keyreply-popup-bubble"
        >
          <div>
            <div
              id="closeButton2"
              class="relative -top-[10px] -right-[10px] float-right cursor-pointer"
              @click.prevent="closeMessagePreview($event)"
            >
              <el-icon><Close /></el-icon>
            </div>
            {{
              previewBubble.persistPreviewMessageShown
                ? previewBubble.message
                : previewBubble.maxLimitReachedMessage ||
                  "We're experiencing high traffic. Please try again soon. Thank you for your patience!"
            }}
          </div>
        </el-card>
      </transition>
    </div>

    <chat-window
      ref="chatwindow"
      v-if="chatWindow"
      @handleGrabElement="handleGrabElement"
      :isWidgetReadyToDrag="isElementReadyToDrag"
      :widgetPos="widgetPos"
    />

    <el-dialog
      :close-on-click-modal="false"
      v-model="lightboxVisible"
      :class="[
        'iframeDialog',
        'iframeDialog__mobile-native',
        { 'iframeDialog--mobile-query': $device.isMobileQuery },
      ]"
      custom-class="el-dialog__mobile-native"
      :lock-scroll="true"
      :before-close="beforeClose"
      @close="lightboxContent = ''"
    >
      <div class="scroll-wrapper scroll-wrapper__mobile-native">
        <iframe :src="lightboxContent" class="w-full h-full" frameborder="0" />
      </div>
    </el-dialog>

    <el-drawer
      v-if="isMobileQuery"
      v-model="jwtDrawerEnabled"
      direction="btt"
      :size="'35%'"
      :show-close="false"
      :wrapperClosable="false"
      :close-on-press-escape="false"
    >
      <template #header>
        <span class="mt-5">
          <el-row type="flex" class="items-center">
            <el-icon
              v-if="jwtDrawer.iconType === 'warning'"
              class="text-[30px] text-[#e6a23c] mr-[5px]"
            ><Warning /></el-icon>
            <el-icon v-if="jwtDrawer.iconType === 'info'" class="text-[30px] mr-[5px]"><InfoFilled /></el-icon>
            {{ jwtDrawer.title }}
          </el-row>
        </span>
      </template>
      <el-container class="p-[10px]">
        <el-main class="pt-0">
          <span>{{ jwtDrawer.text }}</span>
        </el-main>
        <el-footer>
          <el-row :gutter="10">
            <el-col :xs="12" :sm="{ span: 6, offset: 12 }">
              <el-button
                class="w-full"
                v-if="jwtDrawer.cancel"
                :type="buttonOutline"
                plain
                @click="jwtDrawerEnabled = false"
                >Cancel</el-button
              >
            </el-col>
            <el-col
              :xs="jwtDrawer.cancel ? 12 : { span: 12, offset: 12 }"
              :sm="jwtDrawer.cancel ? 6 : { span: 6, offset: 18 }"
            >
              <el-button class="w-full" :type="buttonOutline" @click="handleCloseDrawer"
                >Ok</el-button
              >
            </el-col>
          </el-row>
        </el-footer>
      </el-container>
    </el-drawer>

    <el-dialog
      v-else
      :width="'40%'"
      v-model="jwtDrawerEnabled"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <template #header>
        <span class="mt-5">
          <el-row type="flex" class="items-center">
            <el-icon
              v-if="jwtDrawer.iconType === 'warning'"
              class="text-[30px] text-[#e6a23c] mr-[5px]"
            ><Warning /></el-icon>
            <el-icon v-if="jwtDrawer.iconType === 'info'" class="text-[30px] mr-[5px]"><InfoFilled /></el-icon>
            {{ jwtDrawer.title }}
          </el-row>
        </span>
      </template>
      <el-container class="p-[10px]">
        <el-main class="pt-0">
          <span>{{ jwtDrawer.text }}</span>
        </el-main>
      </el-container>
      <template #footer>
        <span>
          <el-row :gutter="10">
            <el-col :xs="12" :sm="{ span: 6, offset: 12 }">
              <el-button
                class="w-full"
                v-if="jwtDrawer.cancel"
                :type="buttonOutline"
                plain
                @click="jwtDrawerEnabled = false"
                >Cancel</el-button
              >
            </el-col>
            <el-col
              :xs="jwtDrawer.cancel ? 12 : { span: 12, offset: 12 }"
              :sm="jwtDrawer.cancel ? 6 : { span: 6, offset: 18 }"
            >
              <el-button class="w-full" :type="buttonOutline" @click="handleCloseDrawer"
                >Ok</el-button
              >
            </el-col>
          </el-row>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
/**
 * @module App
 * @description Root component for the KeyReply webchat widget
 * @vue3.5migration - Phase 2: Composition API Migration
 * - Converted from Options API to Composition API with <script setup>
 * - Migrated template refs to useTemplateRef() for type safety
 * - Replaced direct DOM manipulation with reactive patterns
 * - Optimized watchers for better performance
 */
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useTemplateRef, getCurrentInstance } from 'vue'
import { ElMessageBox } from 'element-plus'
import { Close, Warning, InfoFilled } from '@element-plus/icons-vue'
import { h } from 'vue'
import Launcher from "./components/Launcher.vue"
import AppIcon from "./components/AppIcon.vue"
import ChatWindow from "./components/ChatWindow/Index.vue"
import { get as _get } from "lodash-es"
import { useMainStore } from "@/stores/main"
import getParamsFromUrl from "./helpers/getParamsFromUrl"
import type { ComponentPublicInstance } from 'vue'
import { useVisibleApps } from "@/composables/useVisibleApps"
import { useNavigationPrompt } from "@/composables/useNavigationPrompt"
import { useWidgetDrag } from "@/composables/useWidgetDrag"

// ====================
// Type Interfaces for Component Refs
// ====================

/**
 * Exposed methods from ChatWindow component
 */
interface ChatWindowExposed {
  scrollToBottom: () => void
}

/**
 * Exposed methods from Launcher component
 */
interface LauncherExposed {
  launcherClicked: () => void
}

// Get store instance
const store = useMainStore()

// Get current instance for $device access
const instance = getCurrentInstance()
const $device = instance?.appContext.config.globalProperties.$device

// ====================
// Reactive State (data properties)
// ====================

/** User's unique ID from localStorage */
const id = ref<string | null>(typeof localStorage !== "undefined" ? localStorage._kruid : null)

/** Current channel (unused?) */
const channel = ref<string | null>(null)

/** Browser window height for icon layout calculations */
const windowHeight = ref(1000)

/** Whether multi-app icons are visible */
const showIcons = ref(false)

/** URL content for lightbox iframe */
const lightboxContent = ref("")

/** Whether greeting has been shown */
const greeted = ref(false)

/** Timer reference (unused?) */
const t = ref(0)

// ====================
// Template Refs (Vue 3.5 useTemplateRef)
// ====================

const launcher = useTemplateRef<ComponentPublicInstance>('launcher')
const chatwindow = useTemplateRef<ComponentPublicInstance>('chatwindow')
const container = useTemplateRef<HTMLElement>('container')

// ====================
// Computed Properties
// ====================

/**
 * Whether to show the message preview bubble
 */
const showMessagePreview = computed(() => store.showMessagePreview)

/**
 * Whether to display the launcher button
 */
const displayLauncher = computed(() => store.displayLauncher)

/**
 * Check if UI Beta features are enabled
 */
const isUiBetaEnabled = computed(() => store.isUiBetaEnabled)

/**
 * Check if RTL mode is enabled
 */
const isRtlModeEnabled = computed(() => store.isRtlModeEnabled)

/**
 * Computed property for lightbox v-model binding
 */
const lightboxVisible = computed({
  get() {
    return store.lightbox
  },
  set(value: boolean) {
    if (value) {
      store.openDialog()
    } else {
      store.closeDialogMutation()
    }
  },
})

/**
 * Computed property for jwtDrawer.enabled v-model binding
 */
const jwtDrawerEnabled = computed({
  get() {
    return store.jwtDrawer?.enabled || false
  },
  set(value: boolean) {
    if (store.jwtDrawer) {
      store.jwtDrawer.enabled = value
    }
  },
})

/**
 * Check if running in mobile mode via URL parameter
 */
const isMobileQuery = computed(() => {
  const params = getParamsFromUrl()
  return params.get("mode") === "mobile"
})

/**
 * Map button color setting to Element UI button type
 */
const buttonOutline = computed(() => {
  switch (settings.value.buttonOutlineColor) {
    case "red":
      return "danger"
    case "blue":
      return "primary"
    case "green":
      return "success"
    default:
      return "primary"
  }
})

/**
 * JWT session drawer state
 */
const jwtDrawer = computed(() => store.jwtDrawer)

/**
 * Access main settings from Pinia store
 */
const settings = computed(() => store.settings)

/**
 * Preview bubble configuration
 */
const previewBubble = computed(() => {
  const previewBubbleSettings = _get(settings.value, "previewBubble", {
    enabled: false,
    message: "",
    delay: 0,
    hide: 0,
    maxLimitReachedMessage: "",
    persistPreviewMessageShown: false,
  })
  return previewBubbleSettings
})

/**
 * CSS class for launcher position
 */
const launcherPosition = computed(() =>
  settings.value.position === "left" ? "keyreply-left" : "keyreply-right"
)

// ====================
// Composables
// ====================

/**
 * Extract visible apps logic to composable for better organization
 */
const { visibleApps, numberOfApps } = useVisibleApps(showIcons, settings)

/**
 * Widget drag functionality extracted to composable
 */
const {
  isElementReadyToDrag,
  widgetPos,
  handleGrabElement,
} = useWidgetDrag({
  isWidgetDraggable: computed(() => settings.value.isWidgetDraggable),
  isUiBetaEnabled,
  isMobile: $device?.Mobile ?? false,
  windowSize: computed(() => settings.value.window),
  launcherRef: launcher,
  chatWindowRef: chatwindow,
})

/**
 * Whether to show "more" icon for overflow apps
 */
const showMoreIcon = computed(() => numberOfApps.value > maxIconCount.value)

/**
 * Maximum number of app icons that fit in viewport
 */
const maxIconCount = computed(() => Math.floor((windowHeight.value - 130) / 52))

/**
 * Chat window visibility state from store
 */
const chatWindow = computed(() => store.chatWindow)

/**
 * Whether currently in a live chat session
 */
const inLivechatSession = computed(() => settings.value.liveChatStatus)

/**
 * Extract navigation prompt logic to composable for better organization
 */
const { shouldPromptBeforeNavigateAway } = useNavigationPrompt(chatWindow, settings)

// ====================
// Methods (converted to functions)
// ====================

/**
 * Handle notification drawer close
 */
function handleCloseDrawer() {
  if (store.jwtDrawer) {
    store.jwtDrawer.enabled = false
  }
  store.setGreeted(false)
  store.clearMessages()
  localStorage.clear()

  const drawerAction = _get(jwtDrawer.value, "action")
  if (drawerAction) {
    drawerAction()
  }
}

/**
 * Launch webchat automatically in mobile mode
 */
function launchingAutomatically() {
  const params = new URLSearchParams(location.search)
  const webchatUsersIsAtMaxLimit = store.webchatUsersIsAtMaxLimit
  const isNotMobileMode = params.get("mode") !== "mobile"

  if (webchatUsersIsAtMaxLimit || isNotMobileMode) {
    return
  }

  if (checkConditionToLaunchWebchat()) {
    store.start()
    store.openChatWindow()
  } else {
    showIcons.value = !showIcons.value
  }

  clickedLauncher()
}

/**
 * Check if conditions are met to launch webchat
 */
function checkConditionToLaunchWebchat() {
  const numberOfAppsIsOne = numberOfApps.value === 1
  const settingsAppsBotIsEnabled = _get(settings.value, "apps.bot.enabled")
  return numberOfAppsIsOne && settingsAppsBotIsEnabled
}

/**
 * Handle widget close event (beforeunload)
 */
function handleWidgetClose(event: BeforeUnloadEvent) {
  if (shouldPromptBeforeNavigateAway.value) {
    event.preventDefault()
    event.returnValue = ""
  }
}

/**
 * Trigger preview bubble with delay
 */
function triggerPreviewDelay() {
  if (previewBubble.value.enabled && !showMessagePreview.value) {
    setTimeout(() => {
      store.updateShowPreviewBubble({ status: true })
      if (previewBubble.value.hide == 0) {
        return
      }

      setTimeout(() => {
        store.updateShowPreviewBubble({ status: false })
      }, previewBubble.value.hide * 1000)
    }, previewBubble.value.delay * 1000)
  }
}

/**
 * Close message preview bubble
 */
function closeMessagePreview(e: Event) {
  e.stopPropagation()
  store.updateShowPreviewBubble({ status: false })
}

/**
 * Handle launcher click when only one app is configured
 */
function clickedLauncher() {
  if (settings.value.apps.length === 1) {
    if (!store.webchatUsersIsAtMaxLimit) {
      store.updateAgentTypingIndicator(false)
      store.openChatWindowMutation()
      store.clearUnreadMessageCount()
      store.start()

      nextTick(() => {
        const chatWindowComponent = chatwindow.value as (ComponentPublicInstance & ChatWindowExposed) | null
        chatWindowComponent?.scrollToBottom()
      })
    }
  }
}

/**
 * Before close handler for lightbox dialog
 */
function beforeClose(done: () => void) {
  const confirmMessage = h(
    "span",
    { style: "font-family: var(--keyreply-font-family);" },
    "Are you sure you want to close this window?"
  )

  // Note: Element Plus types don't fully support VNode in confirm message parameter
  ElMessageBox.confirm(confirmMessage as any, "", {
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    confirmButtonClass: "confirm-button-text",
    cancelButtonClass: "cancel-button-text",
  })
    .then(() => {
      done()
    })
    .catch(() => {})
}

/**
 * Click launcher programmatically
 */
function clickLauncher() {
  const launcherComponent = launcher.value as (ComponentPublicInstance & LauncherExposed) | null
  launcherComponent?.launcherClicked()
}

/**
 * Setup RTL mode on document element
 */
function setupRtlMode(isRtlModeEnabled: boolean) {
  const htmlEl = document.documentElement
  if (isRtlModeEnabled) {
    htmlEl.setAttribute("dir", "rtl")
  } else {
    htmlEl.setAttribute("dir", "ltr")
  }
}

// ====================
// Watchers
// ====================

// Watch displayLauncher for preview trigger
watch(displayLauncher, (newValue) => {
  if (newValue) {
    triggerPreviewDelay()
  }
})

// Watch settings.apps.bot for automatic launching
watch(
  () => settings.value.apps?.bot,
  () => {
    launchingAutomatically()
  },
  { deep: true }
)

// Watch RTL mode changes
watch(isRtlModeEnabled, (newValue) => {
  setupRtlMode(newValue)
})

// ====================
// Lifecycle Hooks
// ====================

// Store handler references for cleanup
let handleBeforeUnload: ((event: BeforeUnloadEvent) => void) | null = null
let handleResize: (() => void) | null = null
let handleOnlineStatus: (() => void) | null = null

onMounted(() => {
  setTimeout(() => {
    store.updateInitializingDone()
  }, 5000)

  const params = location.search
  const startImmediately = params ? !params.includes("manual=true") : true

  if (startImmediately) {
    store.initialize()
  }

  setupRtlMode(isRtlModeEnabled.value)

  // Use template ref instead of getElementById
  if (container.value) {
    container.value.onwheel = (e: WheelEvent) => {
      e.stopPropagation()
    }
  }

  // Store handler references for cleanup
  handleBeforeUnload = (event: BeforeUnloadEvent) => {
    handleWidgetClose(event)
  }
  handleResize = () => {
    windowHeight.value = window.innerHeight
  }
  handleOnlineStatus = () => {
    store.updateOnlineStatus()
  }

  window.addEventListener("beforeunload", handleBeforeUnload)

  nextTick(() => {
    windowHeight.value = window.innerHeight
    window.addEventListener("resize", handleResize!)
    window.addEventListener("online", handleOnlineStatus!)
    window.addEventListener("offline", handleOnlineStatus!)
  })
})

/**
 * Cleanup event listeners before component unmounts
 */
onBeforeUnmount(() => {
  if (handleBeforeUnload) {
    window.removeEventListener("beforeunload", handleBeforeUnload)
  }
  if (handleResize) {
    window.removeEventListener("resize", handleResize)
  }
  if (handleOnlineStatus) {
    window.removeEventListener("online", handleOnlineStatus)
    window.removeEventListener("offline", handleOnlineStatus)
  }

  // Clean up RTL mode
  document.documentElement.removeAttribute("dir")
})
</script>

<style lang="scss">
$font-stack: "ProximaNova", Avenir, "Tahoma", "Avenir Next", -apple-system, BlinkMacSystemFont,
  "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
  "Segoe UI Symbol";

@media only screen and (max-width: 768px) {
  .el-message-box {
    width: 420px;
  }

  .el-message-box__content {
    font-size: 20px;
  }

  .confirm-button-text {
    font-size: 20px;
  }

  .cancel-button-text {
    font-size: 20px;
  }

  .cbio_session_controls {
    bottom: 60px !important;
  }
}

@media only screen and (max-width: 414px) {
  .el-message-box {
    width: 300px;
  }

  .el-message-box__content {
    font-size: 16px;
  }

  .confirm-button-text {
    font-size: 12px;
  }

  .cancel-button-text {
    font-size: 12px;
  }
}

#keyreply-container {
  font-family: $font-stack;
  font-weight: 400;
  z-index: 99999;

  .keyreply-launcher-wrapper {
    position: fixed;
    right: 0;
    bottom: 0;
    z-index: 100;
    padding: 20px;
    display: flex;
    flex-direction: column-reverse;
    align-items: flex-end;
    pointer-events: none;
  }

  &.keyreply-left .keyreply-launcher-wrapper {
    right: auto;
    left: 0;
  }

  &.keyreply-left .keyreply-launcher-wrapper .keyreply-launcher,
  &.keyreply-left .keyreply-launcher-wrapper .keyreply-chat-icon {
    right: auto;
    left: 20px;
  }

  &.keyreply-left .keyreply-launcher-wrapper .keyreply-chat-icon.keyreply-panel {
    left: auto;
  }
  //HACKY WAY TO PREVENT OVERRIDING TEXTAREA HEIGHT
  .keyreply-message-input {
    textarea {
      height: 38px;
      min-height: 38px;
      max-height: 200px;
      overflow-y: auto;
      resize: none;
      background: #fff;
      outline: none;
      border: none;
    }
  }
}

.el-input__inner {
  -webkit-appearance: none;
  background-color: #fff;
  background-image: none;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
}

.keyreply-popup-bubble {
  width: 280px;
  border-radius: 10px;
  overflow: hidden;
  -o-text-overflow: ellipsis;
  text-overflow: ellipsis;
  pointer-events: auto;
  position: absolute;
  bottom: 100%;
  margin: 0 20px;
  color: var(--keyreply-agent-text-color);
  background: var(--keyreply-agent-bubble-color);

  @media only screen and (max-width: 330px) {
    width: 180px;
    height: 80px;
    font-size: small;
    right: 80px;
  }

  @media only screen and (min-width: 360px) {
    width: 230px;
  }
}

.el-dropdown-menu__item {
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei",
    "微软雅黑", Arial, sans-serif;
}

.el-dialog {
  position: relative;
  margin-top: 6vh !important;
  background: #fff;
  border-radius: 2px;
  -webkit-box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
  width: 70%;
  margin-top: 3vh !important;

  @media only screen and (max-width: 768px) {
    top: 1vh !important;
    bottom: 1vh !important;
    height: 100%;
    width: 90% !important;
  }

  @media only screen and (max-width: 450px) {
    margin-top: 6vh !important;
    width: 100% !important;
  }
}

.el-dialog__mobile-native {
  @media only screen and (max-width: 450px) {
    top: 0px !important;
    margin-top: 0px !important;
    margin-bottom: 0px !important;
  }
}

.iframeDialog__mobile-native {
  @media only screen and (max-width: 450px) {
    top: 0px !important;
    height: 100% !important;
  }
}

.iframeDialog {
  margin-top: 10px !important;
  width: 80% !important;
  overflow: visible !important;

  @media only screen and (max-width: 1024px) {
    width: 100% !important;
    height: 90%;
  }
}

.iframeDialog--mobile-query {
  margin-top: 0px !important;
}

.scroll-wrapper {
  -webkit-overflow-scrolling: touch;
  overflow: hidden;
  height: 80vh;
  width: 100%;
}

.scroll-wrapper__mobile-native {
  @media only screen and (max-width: 450px) {
    height: calc(100vh - 90px);
  }
}

/* Media Query */

#keyreply-container img {
  max-width: 100%;
  max-height: 95vh;
}

.has-cursor-pointer {
  cursor: pointer;
}

.is-mobile-hidden {
  @media only screen and (max-width: 600px) {
    display: none;
  }
}

@media print {
  #keyreply-container {
    visibility: hidden;
  }
}
</style>

<style lang="scss">
[dir="ltr"] #keyreply-container {
  .keyreply-launcher-wrapper {
    right: 0;
  }

  &.keyreply-left .keyreply-launcher-wrapper {
    right: auto;
    left: 0;
  }

  &.keyreply-left .keyreply-launcher-wrapper .keyreply-launcher,
  &.keyreply-left .keyreply-launcher-wrapper .keyreply-chat-icon {
    right: auto;
    left: 20px;
  }

  &.keyreply-left .keyreply-launcher-wrapper .keyreply-chat-icon.keyreply-panel {
    left: auto;
  }
}

[dir="rtl"] #keyreply-container {
  .keyreply-launcher-wrapper {
    left: 0;
  }

  &.keyreply-left .keyreply-launcher-wrapper {
    left: auto;
    right: 0;
  }

  &.keyreply-left .keyreply-launcher-wrapper .keyreply-launcher,
  &.keyreply-left .keyreply-launcher-wrapper .keyreply-chat-icon {
    left: auto;
    right: 20px;
  }

  &.keyreply-left .keyreply-launcher-wrapper .keyreply-chat-icon.keyreply-panel {
    right: auto;
  }
}

[dir="ltr"] .text-left {
  text-align: left;
}

[dir="rtl"] .text-left {
  text-align: right;
}

[dir="ltr"] .text-right {
  text-align: right;
}

[dir="rtl"] .text-right {
  text-align: left;
}

[dir="ltr"] .mr-5 {
  margin-right: 5px;
}

[dir="rtl"] .mr-5 {
  margin-left: 5px;
}

[dir="ltr"] .mr-12 {
  margin-right: 12px;
}

[dir="rtl"] .mr-12 {
  margin-left: 12px;
}

[dir="ltr"] .mr-36 {
  margin-right: 36px;
}

[dir="rtl"] .mr-36 {
  margin-left: 36px;
}

[dir="ltr"] .m-0-5-10-n10 {
  margin: 0 5px 10px -10px;
}

[dir="rtl"] .m-0-5-10-n10 {
  margin: 0 -10px 10px 5px;
}

[dir="ltr"] .right-5p {
  right: 5%;
}

[dir="rtl"] .right-5p {
  left: 5%;
}

[dir="ltr"] .left-5p {
  left: 5%;
}

[dir="rtl"] .left-5p {
  right: 5%;
}

[dir="ltr"] .float-right {
  float: right;
}

[dir="rtl"] .float-right {
  float: left;
}

[dir="ltr"] .right-n10 {
  right: -10px;
}

[dir="rtl"] .right-n10 {
  left: -10px;
}

[dir="ltr"] .right-0 {
  right: 0;
}

[dir="rtl"] .right-0 {
  left: 0;
}

[dir="ltr"] .right-130 {
  right: 130px;
}

[dir="rtl"] .right-130 {
  left: 130px;
}

[dir="ltr"] .right-100 {
  right: 100px;
}

[dir="rtl"] .right-100 {
  left: 100px;
}
</style>
